import { useEffect, useState } from "react";
import { Lock, Sparkles, ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, betaState, betaUnlock, betaLaunchList } from "@/lib/api";

/**
 * Splash gate for the Early Access Preview (3 – 23 August 2026).
 *
 * Rules:
 *   • Renders nothing unless backend `/api/beta/state` returns `phase=active`
 *     or `phase=closed` AND the visitor is not yet unlocked (no cookie / no tester record).
 *   • When phase === "closed" (the programme is turned off indefinitely) we
 *     show a minimal "Coming Soon" splash with no signup surfaces at all.
 *     Bypass token + admin cookie holders still get through to the real site.
 *   • An invite code can be prefilled via `?invite=AC-…` query param.
 *   • Admins logged into the admin panel never see this — they hold the
 *     `ashlan_admin_session` cookie, which we treat as a bypass on first
 *     render to avoid blocking internal previews.
 *   • A `?previewbeta=1` query param force-renders the gate (for QA).
 */
export default function BetaGate() {
  const [state, setState] = useState(null); // null = loading, false = no gate
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [welcome, setWelcome] = useState(null);
  // "no-invite" / "we're full" view state
  const [noInviteView, setNoInviteView] = useState(false);
  const [llEmail, setLlEmail] = useState("");
  const [llName, setLlName] = useState("");
  const [llSubmitting, setLlSubmitting] = useState(false);
  const [llSubmitted, setLlSubmitted] = useState(false);

  useEffect(() => {
    // Staff Portal is ALWAYS reachable regardless of beta phase — the whole
    // point of `/staff/login` and `/staff` is for internal staff to log into
    // Pabau / Marketing without needing a beta bypass token.
    if (typeof window !== "undefined") {
      const p = window.location.pathname;
      if (p === "/staff" || p === "/staff/login" || p.startsWith("/staff/")) {
        setState(false);
        return undefined;
      }
    }

    // 1) Owner / stakeholder shareable bypass link: ?bypass=TOKEN
    //    Hit the backend to set a long-lived HttpOnly cookie, clean the URL,
    //    then continue with state load (which will report is_bypass=true).
    let bypassPromise = Promise.resolve();
    try {
      const url = new URL(window.location.href);
      const bypassToken = url.searchParams.get("bypass");
      const inv = url.searchParams.get("invite");
      if (inv) setCode(inv);
      if (bypassToken) {
        bypassPromise = api
          .get(`/beta/bypass`, { params: { token: bypassToken } })
          .catch(() => {})
          .finally(() => {
            try {
              const clean = new URL(window.location.href);
              clean.searchParams.delete("bypass");
              window.history.replaceState({}, "", clean.toString());
            } catch {
              /* noop */
            }
          });
      }
    } catch {
      /* noop */
    }
    bypassPromise
      .then(() => betaState())
      .then((s) => {
        const force =
          typeof window !== "undefined" &&
          new URL(window.location.href).searchParams.get("previewbeta") === "1";
        if (force) {
          setState(s);
          return;
        }
        // Admins and bypass-link visitors never see the gate.
        if (s.is_admin || s.is_bypass || s.unlocked) {
          setState(false);
          return;
        }
        if (s.phase === "active" || s.phase === "closed") {
          setState(s);
        } else {
          setState(false);
        }
      })
      .catch(() => {
        // Fail CLOSED: if we can't confirm phase/unlock/bypass, keep the gate
        // up with a minimal error view — but still honour any pre-existing
        // bypass/unlock HttpOnly cookie the server would have recognised.
        // (Those visitors will pass on the next successful state fetch.)
        setState({
          phase: "active",
          end: null,
          public_launch_date: null,
          capacity_left: 30,
          _error: true,
        });
      });
  }, []);

  if (state === null || state === false) return null;

  // ---------- Programme closed: minimal "Coming Soon" splash ----------
  // No signup surfaces of any kind. Bypass + admin cookie holders never reach
  // this code path (they're handed setState(false) above).
  if (state.phase === "closed") {
    return (
      <div
        className="fixed inset-0 z-[9000] bg-[#080808] flex items-center justify-center p-4"
        data-testid="beta-gate-coming-soon"
        role="dialog"
        aria-modal="true"
        aria-labelledby="beta-gate-title"
      >
        <div className="max-w-lg w-full text-center px-6">
          <div className="inline-flex items-center gap-2 mb-8">
            <Sparkles size={14} className="text-[#9DD4D8]" />
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[#9DD4D8]">
              Ashlan Clinic · Bermuda
            </span>
          </div>
          <h1
            id="beta-gate-title"
            className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-tight"
          >
            Coming soon.
          </h1>
          <p className="mt-6 text-[#B8BEC7] leading-relaxed text-base md:text-lg">
            Our doors are open — and inside, transformative clinical massage therapy
            is already at work across Bermuda. Our new website is launching shortly.
          </p>
          <div className="mt-10 h-px w-16 bg-[#3A7A8C] mx-auto" aria-hidden />
          <p className="mt-10 text-xs uppercase tracking-[0.22em] text-[#6B7280]">
            Thank you for your patience.
          </p>
        </div>
      </div>
    );
  }

  // ---------- Active-phase paths below (invite unlock + launch list) ----------

  // When we hit the 30-tester cap, default to the "no-invite" view so the
  // visitor lands on the warm thank-you instead of a form they can't use.
  // Visitors who actually have a code (i.e. `?invite=` query) get the form
  // regardless so they're not blocked.
  const capacityFull = (state.capacity_left ?? 0) <= 0;
  const showCapacityView = (capacityFull && !code) || noInviteView;
  const launchDate = state.public_launch_date || state.end;
  let prettyLaunch = "24 August 2026";
  try {
    prettyLaunch = new Date(launchDate).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    /* noop */
  }

  // Build a friendly countdown string from the server-supplied end date.
  let daysLeftLine = "";
  try {
    const end = new Date(state.end);
    const now = new Date();
    const diffMs = end.getTime() - now.getTime();
    const days = Math.max(0, Math.ceil(diffMs / 86400000));
    if (days === 0) daysLeftLine = "Closing today";
    else if (days === 1) daysLeftLine = "1 day left";
    else daysLeftLine = `${days} days left`;
  } catch {
    /* noop */
  }
  if (welcome) {
    return (
      <div
        className="fixed inset-0 z-[9000] bg-[#080808]/85 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="beta-gate-welcome"
      >
        <div className="bg-white max-w-xl w-full p-8 md:p-12 border-t-4 border-[#5FA8B4]">
          <CheckCircle2 size={40} className="text-[#3A7A8C]" />
          <h2 className="mt-5 text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight">
            Welcome in, {welcome.name.split(" ")[0]}.
          </h2>
          <p className="mt-5 text-[#3C4048] leading-relaxed">
            {welcome.tier === "full_experience" ? (
              <>
                You&apos;re here as a <strong>Full Experience</strong> tester. Book a real session,
                attend, pay as normal — then share your honest reflection on the whole journey.
                Your feedback is the most valuable signal we&apos;ll receive before launch.
              </>
            ) : (
              <>
                You&apos;re here as a <strong>Site Tester</strong>. Explore freely, chat with the
                AI Concierge, and tell us what works (and what doesn&apos;t).
              </>
            )}
          </p>
          <div className="mt-6 bg-[#F5F5F5] border-l-2 border-[#5FA8B4] p-5">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">
              Thank-you gift
            </p>
            <p className="mt-2 text-[#3C4048] text-sm leading-relaxed">
              A <strong>10% gift voucher</strong> is yours after the public launch on 24 August
              2026. Quote{" "}
              <span className="font-mono font-semibold text-[#080808]">{welcome.promo_code}</span>{" "}
              when you book your next appointment.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setWelcome(null);
              setState(false);
              // Clear ?invite= query so the URL is clean while browsing.
              try {
                const u = new URL(window.location.href);
                u.searchParams.delete("invite");
                window.history.replaceState({}, "", u.toString());
              } catch {
                /* noop */
              }
            }}
            data-testid="beta-gate-enter"
            className="ash-btn-primary mt-8 w-full sm:w-auto"
          >
            Enter the Preview <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !code.trim()) {
      toast.error("Name, email and invite code are all required.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await betaUnlock({
        name: name.trim(),
        email: email.trim(),
        code: code.trim(),
      });
      setWelcome(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Couldn't verify your invite.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const submitLaunchList = async (e) => {
    e.preventDefault();
    if (!llEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(llEmail.trim())) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLlSubmitting(true);
    try {
      await betaLaunchList({ name: llName.trim() || null, email: llEmail.trim(), hp: "" });
      setLlSubmitted(true);
      toast.success("You're on the list — we'll be in touch on launch day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save your email — try again?");
    } finally {
      setLlSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9000] bg-[#080808]/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="beta-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="beta-gate-title"
    >
      <div className="bg-white max-w-xl w-full p-8 md:p-12 border-t-4 border-[#5FA8B4] shadow-2xl">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#3A7A8C]" />
            <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#3A7A8C]">
              Early Access Preview · 3 – 23 August 2026
            </span>
          </div>
          {daysLeftLine && (
            <span
              data-testid="beta-gate-countdown"
              className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#3C4048] bg-[#F5F5F5] px-2.5 py-1"
            >
              {daysLeftLine}
            </span>
          )}
        </div>
        <h1
          id="beta-gate-title"
          className="text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight"
        >
          {showCapacityView
            ? "Thank you for stopping by."
            : "A private preview, before we open the doors."}
        </h1>
        {showCapacityView ? (
          <>
            <p className="mt-5 text-[#3C4048] leading-relaxed">
              We&apos;ve filled the 30 places in our Early Access Preview — thank you for the
              enthusiasm. Ashlan Clinic launches publicly on <strong>{prettyLaunch}</strong>.
            </p>
            <p className="mt-3 text-[#3C4048] leading-relaxed">
              Leave us your email and we&apos;ll send you a single message on launch day so you
              can be among the first through the door.
            </p>
            {llSubmitted ? (
              <div
                data-testid="beta-gate-ll-success"
                className="mt-6 bg-[#F5F5F5] border-l-2 border-[#5FA8B4] p-5 flex items-start gap-3"
              >
                <CheckCircle2 size={20} className="text-[#3A7A8C] shrink-0 mt-0.5" />
                <p className="text-sm text-[#3C4048] leading-relaxed">
                  You&apos;re on the launch list. See you on <strong>{prettyLaunch}</strong>.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submitLaunchList}
                className="mt-6 grid gap-3"
                data-testid="beta-gate-launch-list-form"
                noValidate
              >
                <input
                  type="text"
                  value={llName}
                  onChange={(e) => setLlName(e.target.value)}
                  placeholder="Your name (optional)"
                  data-testid="beta-gate-ll-name"
                  className="w-full px-4 py-3 border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C]"
                />
                <input
                  type="email"
                  value={llEmail}
                  onChange={(e) => setLlEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="beta-gate-ll-email"
                  className="w-full px-4 py-3 border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C]"
                />
                <button
                  type="submit"
                  disabled={llSubmitting}
                  data-testid="beta-gate-ll-submit"
                  className="ash-btn-primary disabled:opacity-60"
                >
                  <Mail size={16} /> {llSubmitting ? "Saving…" : "Notify me at launch"}
                </button>
              </form>
            )}
            {noInviteView && capacityFull === false && (
              <button
                type="button"
                onClick={() => setNoInviteView(false)}
                data-testid="beta-gate-back-to-form"
                className="mt-6 text-xs text-[#3A7A8C] hover:underline"
              >
                ← I do have an invite after all
              </button>
            )}
          </>
        ) : (
          <>
            <p className="mt-5 text-[#3C4048] leading-relaxed">
              Ashlan Clinic launches publicly on <strong>{prettyLaunch}</strong>. You&apos;ve been
              invited to try the site privately for two weeks, give us your honest feedback, and
              help us launch in the best possible shape.
            </p>
            <form onSubmit={submit} className="mt-8 grid gap-4" noValidate>
              <div>
                <label
                  htmlFor="beta-name"
                  className="block text-sm font-semibold text-[#080808] mb-2"
                >
                  Full name
                </label>
                <input
                  id="beta-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="beta-name"
                  autoComplete="name"
                  required
                  className="w-full px-4 py-3 border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C] transition-colors"
                />
              </div>
              <div>
                <label
                  htmlFor="beta-email"
                  className="block text-sm font-semibold text-[#080808] mb-2"
                >
                  Email (must match your invite)
                </label>
                <input
                  id="beta-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="beta-email"
                  autoComplete="email"
                  required
                  className="w-full px-4 py-3 border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C] transition-colors"
                />
              </div>
              <div>
                <label
                  htmlFor="beta-code"
                  className="block text-sm font-semibold text-[#080808] mb-2"
                >
                  Invite code
                </label>
                <input
                  id="beta-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  data-testid="beta-code"
                  placeholder="AC-XXXX-XXXX"
                  required
                  className="w-full px-4 py-3 border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C] transition-colors font-mono"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="beta-submit"
                className="ash-btn-primary mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Lock size={16} /> {submitting ? "Verifying…" : "Enter the Preview"}
              </button>
            </form>
            <button
              type="button"
              onClick={() => setNoInviteView(true)}
              data-testid="beta-gate-no-invite"
              className="mt-5 text-xs text-[#6B7280] hover:text-[#080808] underline"
            >
              I don&apos;t have an invite
            </button>
          </>
        )}
        <p className="mt-6 text-xs text-[#6B7280] leading-relaxed">
          By entering, you agree we may store your name and email for the duration of the beta
          and add you to our launch list. We never share your details with third parties.
        </p>
      </div>
    </div>
  );
}
